import { defineStore } from "pinia";
import { type Node, AnchorType, SatelliteType } from "@livereader/graphly-d3";
import type {
	ConceptDataobject,
	ConceptFunction,
	ConceptAttribute,
	ConceptEvent,
} from "@lr-notitia/notitia-engineer/schemas";
import { useGraphlyTemplatesStore } from "./graphly-templates";
import { useGraphStore } from "./graph";
import { useDomainStore } from "./domain";
import { useSymbolsStore } from "./symbols";
import { ref } from "vue";
import { ConceptView } from "@lr-notitia/notitia-model-store/schemas";

let graphlyTemplatesStore: ReturnType<typeof useGraphlyTemplatesStore>;
let graphStore: ReturnType<typeof useGraphStore>;
let domainStore: ReturnType<typeof useDomainStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	graphlyTemplatesStore = useGraphlyTemplatesStore();
	graphStore = useGraphStore();
	domainStore = useDomainStore();
	symbolsStore = useSymbolsStore();
}

export const useInspectorStore = defineStore("InspectorStore", () => {
	const history = ref<string[]>([]);
	const historyIndex = ref<number>(-1);

	function pushHistory(id: string) {
		history.value = history.value.slice(0, historyIndex.value + 1);
		history.value.push(id);
		historyIndex.value++;
	}
	function goBack(): string | null {
		if (historyIndex.value > 0) {
			historyIndex.value--;
			return history.value[historyIndex.value];
		}
		return null;
	}
	function goForward(): string | null {
		if (historyIndex.value < history.value.length - 1) {
			historyIndex.value++;
			return history.value[historyIndex.value];
		}
		return null;
	}
	function clearHistory() {
		history.value = [];
		historyIndex.value = -1;
	}

	function dataobjectNode(dataobject: ConceptDataobject, suffix = "", pos = { x: 0, y: 0 }): Node {
		if (!graphlyTemplatesStore || !graphStore || !symbolsStore) init();
		return {
			id: dataobject.id + suffix,
			shape: {
				type: "dataobject",
				scale: 1,
				url: graphlyTemplatesStore.dataobjectShape,
			},
			x: pos.x,
			y: pos.y,
			anchor: {
				type: AnchorType.Hard,
				x: pos.x,
				y: pos.y,
			},
			payload: {
				title: symbolsStore.term({ id: dataobject.refersTo, terminology: dataobject.terminology || {} }),
				stacked: false,
				emphasized: false,
				tags: dataobject.consistsOf
					.map((a) => graphStore.graph.nodes[a] as ConceptAttribute)
					.filter((a) => a)
					.map((a) => symbolsStore.term({ id: a.refersTo, terminology: a.terminology || {} })),
				type: "collection",
				color: dataobject.color,
			},
		};
	}

	function functionNode(func: ConceptFunction, suffix = "", pos = { x: 0, y: 0 }): Node {
		if (!graphlyTemplatesStore || !symbolsStore) init();
		return {
			id: func.id + suffix,
			shape: {
				type: "cell-body",
				scale: 1,
				url: graphlyTemplatesStore.cellBodyShape,
			},
			x: pos.x,
			y: pos.y,
			anchor: {
				type: AnchorType.Hard,
				x: pos.x,
				y: pos.y,
			},
			payload: {
				name: symbolsStore.term({ id: func.refersTo, terminology: func.terminology || {} }),
			},
		};
	}
	function functionInputNode(sourceId: string, suffix = "_input"): Node {
		if (!graphlyTemplatesStore || !graphStore) init();
		return {
			id: sourceId + suffix,
			shape: {
				type: "cell-satellite",
				scale: 1,
				url: graphlyTemplatesStore.cellSatelliteShape,
			},
			satellite: {
				source: sourceId,
				angle: 270,
				distance: 110,
				type: SatelliteType.Hard,
			},
			payload: {
				type: "input",
			},
		};
	}
	function functionOutputNode(sourceId: string, suffix = "_output"): Node {
		if (!graphlyTemplatesStore) init();
		return {
			id: sourceId + suffix,
			shape: {
				type: "cell-satellite",
				scale: 1,
				url: graphlyTemplatesStore.cellSatelliteShape,
			},
			satellite: {
				source: sourceId,
				angle: 90,
				distance: 110,
				type: SatelliteType.Hard,
			},
			payload: {
				type: "output",
			},
		};
	}
	function attributeNode(attribute: ConceptAttribute, suffix = "", pos = { x: 0, y: 0 }): Node {
		if (!graphlyTemplatesStore || !symbolsStore) init();
		return {
			id: attribute.id + suffix,
			shape: {
				type: "gen-pill",
				scale: 1,
				url: graphlyTemplatesStore.genericPill,
			},
			x: pos.x,
			y: pos.y,
			anchor: {
				type: AnchorType.Hard,
				x: pos.x,
				y: pos.y,
			},
			payload: {
				text: symbolsStore.term({ id: attribute.refersTo, terminology: attribute.terminology || {} }),
				color: "#b3e5fc",
			},
		};
	}
	function eventNode(event: ConceptEvent, suffix = "", pos = { x: 0, y: 0 }): Node {
		if (!graphlyTemplatesStore || !symbolsStore) init();
		return {
			id: event.id + suffix,
			shape: {
				type: "event",
				scale: 1,
				url: graphlyTemplatesStore.eventShape,
			},
			x: pos.x,
			y: pos.y,
			anchor: {
				type: AnchorType.Hard,
				x: pos.x,
				y: pos.y,
			},
			payload: {
				mode: "interrupt",
				type: "notitia",
				color: "",
				alignment: "down",
				note: symbolsStore.term({ id: event.refersTo, terminology: event.terminology || {} }),
			},
		};
	}
	function viewNode(view: ConceptView, suffix = "", pos = { x: 0, y: 0 }): Node {
		if (!graphlyTemplatesStore || !domainStore) init();
		return {
			id: view.id + suffix,
			shape: {
				type: "view",
				scale: 2,
				url: graphlyTemplatesStore.viewShape,
			},
			x: pos.x,
			y: pos.y,
			anchor: {
				type: AnchorType.Hard,
				x: pos.x,
				y: pos.y,
			},
			payload: {
				color: "#96C284",
				text: symbolsStore.term({
					id: view.refersTo,
					terminology: view.terminology || {},
					lang: domainStore.languagePreference,
				}),
			},
		};
	}

	return {
		dataobjectNode,
		functionNode,
		attributeNode,
		functionInputNode,
		eventNode,
		functionOutputNode,
		viewNode,
		history,
		historyIndex,
		pushHistory,
		goBack,
		goForward,
		clearHistory,
	};
});
